/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import { Spinner, SpinnerSize, Stack, StackItem, Text } from "@fluentui/react";

import { AzureBenefitExtendCreditForm } from "Components/AzureBenefitExtendCreditForm/AzureBenefitExtendCreditForm";
import KeyBenefitsList from "Components/KeyBenefitsList/KeyBenefitsList";
import M365CopilotBenefitDetail from "Components/M365CopilotBenefitDetail/M365CopilotBenefitDetail";
import { M365DetailsEditForm } from "Components/M365DetailsEditForm/M365DetailsEditForm";
import ProfileAzureBenefitDetail from "Components/ProfileAzureBenefitDetail/ProfileAzureBenefitDetail";
import { SidePanel } from "Components/SidePanel/SidePanel";
import VisualStudioBenefitDetail from "Components/VisualStudioBenefitDetail/VisualStudioBenefitDetail";
import {
  benefitNameToResultMap,
  tenantBenefitTypes,
  BenefitDisplayNameEnum,
  BenefitNameEnum,
} from "Constants/BenefitConstants";
import { OperationStatus } from "Constants/OperationStatus";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { IM365BenefitDetail } from "Interfaces/Benefits/IM365BenefitDetail";
import { IMicrosoft365BenefitDetail } from "Interfaces/Benefits/IMicrosoft365BenefitDetail";
import { IMVPAzureBenefitClaim } from "Interfaces/MVP/IMVPAzureBenefitClaim";
import { IRDAzureBenefitClaim } from "Interfaces/RD/IRDAzureBenefitClaim";
import { getTenant } from "Utility/Utils";

import { AzureBenefitAddRecordForm } from "Components/AzureBenefitAddRecordForm/AzureBenefitAddRecordForm";
import ComboBoxController from "Components/ReactHookForm/ComboBoxController/ComboBoxController";
import BenefitManagementContainer from "Components/BenefitManagement/BenefitManagementContainer";

import { IProfileBenefitDetailsProps } from "./IProfileBenefitDetailsProps";

import {
  benefitLabelStyles,
  containerStyles,
  horizontalGapStackTokens,
} from "./ProfileBenefitDetails.styles";

import { AwardkitMilestone } from "Constants/ProfileConstants";

const ProfileBenefitDetails = (props: IProfileBenefitDetailsProps): React.ReactElement => {
  const {
    profileBenefits,
    profileBenefitsErrorMessage,
    profileBenefitsStatus,
    isClaimingBenefitStatus,
    claimingBenefitError,
    canManageBenefits,
    getProfileBenefits,
    claimBenefitReset,
    claimBenefit,
    claimBenefitManager,
    tenantName,
    m365GetBenefitDetail,
    m365UpdateBenefitDetail,
    m365BenefitDetailReset,
    m365BenefitDetail,
    m365FetchStatus,
    m365UpdateStatus,
    m365FetchErrorMessage,
    m365UpdateErrorMessage,
    ms365GetBenefitDetail,
    ms365UpdateBenefitDetail,
    ms365BenefitDetailReset,
    ms365BenefitDetail,
    ms365FetchStatus,
    ms365UpdateStatus,
    ms365FetchErrorMessage,
    ms365UpdateErrorMessage,
    teamsGetBenefitDetail,
    teamsBenefitDetail,
    teamsFetchStatus,
    teamsFetchErrorMessage,
    awardKitsGetBenefitDetail,
    awardKitsBenefitDetail,
    awardKitsFetchStatus,
    awardKitsFetchErrorMessage,
    extendAzureBenefitCredit,
    extendAzureBenefitCreditUpdateStatus,
    extendAzureBenefitCreditErrorMessage,
    extendAzureBenefitCreditReset,
    addAzureBenefits,
    addAzureBenefitsUpdateStatus,
    addAzureBenefitsErrorMessage,
    addAzureBenefitReset,
  } = props;
  const [isAssignPanelOpen, setIsAssignPanelOpen] = useState(false);
  const [isEditM365PanelOpen, setIsEditM365PanelOpen] = useState(false);
  const [isEditMS365PanelOpen, setIsEditMS365PanelOpen] = useState(false);
  const [isExtendAzureCreditPanelOpen, setIsExtendAzureCreditPanelOpen] = useState(false);
  const [isAddAzureBenefitPanelOpen, setIsAddAzureBenefitPanelOpen] = useState(false);

  const [benefitToClaim, setBenefitToClaim] = useState("");
  const [benefitManagerIdToClaim, setBenefitManagerIdToClaim] = useState("");
  const { id } = useParams<{ id: string }>();
  const [Sku, setSku] = useState(AwardkitMilestone[0].key);
  const isClaimingBenefit = isClaimingBenefitStatus === OperationStatus.Pending;

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getProfileBenefits(parseInt(id)));
    if (m365GetBenefitDetail) {
      dispatch(m365GetBenefitDetail(parseInt(id)));
    }
    if (ms365GetBenefitDetail) {
      dispatch(ms365GetBenefitDetail(parseInt(id)));
    }
    if (teamsGetBenefitDetail) {
      dispatch(teamsGetBenefitDetail(parseInt(id)));
    }
    awardKitsGetBenefitDetail && dispatch(awardKitsGetBenefitDetail(parseInt(id)));
  }, [id]);

  const assignBenefitClicked = (benefitType: string, benefitManagerId?: string): void => {
    setIsAssignPanelOpen(true);
    setBenefitToClaim(benefitType);

    const benefitManagerName = benefitManagerId ? benefitManagerId : "";
    setBenefitManagerIdToClaim(benefitManagerName);
  };

  const editM365DetailsClicked = (): void => {
    setIsEditM365PanelOpen(true);
  };

  const dismissEditM365Panel = (): void => {
    setIsEditM365PanelOpen(false);
    if (m365GetBenefitDetail) {
      dispatch(m365GetBenefitDetail(parseInt(id)));
    }
    if (m365BenefitDetailReset) {
      dispatch(m365BenefitDetailReset());
    }
  };

  const editMS365DetailsClicked = (): void => {
    setIsEditMS365PanelOpen(true);
  };

  const dismissEditMS365Panel = (): void => {
    setIsEditMS365PanelOpen(false);
    if (ms365GetBenefitDetail) {
      dispatch(ms365GetBenefitDetail(parseInt(id)));
    }
    if (ms365BenefitDetailReset) {
      dispatch(ms365BenefitDetailReset());
    }
  };

  const updateMS365BenefitDetail = (ms365BenefitDetail: IMicrosoft365BenefitDetail): void => {
    if (ms365UpdateBenefitDetail) {
      dispatch(ms365UpdateBenefitDetail(ms365BenefitDetail));
    }
    if (ms365UpdateStatus === OperationStatus.Succeeded) {
      dismissEditMS365Panel();
    }
  };

  const dismissAssignPanel = (): void => {
    setIsAssignPanelOpen(false);
    dispatch(getProfileBenefits(parseInt(id)));
    if (m365GetBenefitDetail) {
      dispatch(m365GetBenefitDetail(parseInt(id)));
    }
    dispatch(claimBenefitReset());
  };

  const manageAzureBenefitCreditClicked = (benefitType: string): void => {
    setIsExtendAzureCreditPanelOpen(true);
  };
  const manageAddAzureBenefitClicked = (benefitType: string): void => {
    setIsAddAzureBenefitPanelOpen(true);
  };

  const dismissExtendAzureBenefitCreditPanel = (): void => {
    setIsExtendAzureCreditPanelOpen(false);
    dispatch(getProfileBenefits(parseInt(id)));
    if (m365GetBenefitDetail) {
      dispatch(m365GetBenefitDetail(parseInt(id)));
    }
    if (extendAzureBenefitCreditReset) {
      dispatch(extendAzureBenefitCreditReset());
    }
  };
  const {
    control,
    formState: { errors, isValid },
  } = useForm();

  const dismissAddAzureBenefitPanel = (): void => {
    setIsAddAzureBenefitPanelOpen(false);
    dispatch(getProfileBenefits(parseInt(id)));
    if (m365GetBenefitDetail) {
      dispatch(m365GetBenefitDetail(parseInt(id)));
    }
    addAzureBenefitReset && dispatch(addAzureBenefitReset());
  };

  const extendAzureBenefitCreditDispatch = (credit: number): void => {
    if (extendAzureBenefitCredit) {
      dispatch(extendAzureBenefitCredit(credit, parseInt(id)));
    }
  };
  const addAzureBenefitsDispatch = (
    azureBenefit: IMVPAzureBenefitClaim | IRDAzureBenefitClaim
  ): void => {
    addAzureBenefits && dispatch(addAzureBenefits(azureBenefit, parseInt(id)));
  };

  /**
   * Claim benefit dispatch function
   * @returns {void}
   * @description Dispatches the claimBenefit action with the selected benefit and user profile id.
   * This function is updated to handle the claimBenefitManager action as well.
   */
  const claimBenefitDispatch = (): void => {
    const skuValue = benefitToClaim === BenefitNameEnum.AwardKits ? Sku : "";
    if (benefitManagerIdToClaim) {
      claimBenefitManager && dispatch(claimBenefitManager(benefitManagerIdToClaim, parseInt(id)));
    } else {
      dispatch(claimBenefit(benefitToClaim, parseInt(id), skuValue));
    }
  };

  const updateM365BenefitDetail = (m365BenefitDetail: IM365BenefitDetail): void => {
    if (m365UpdateBenefitDetail) {
      dispatch(m365UpdateBenefitDetail(m365BenefitDetail));
    }
    if (m365UpdateStatus === OperationStatus.Succeeded) {
      dismissEditM365Panel();
    }
  };

  const getAvailableBenefitTypes = (): BenefitNameEnum[] => {
    return tenantBenefitTypes[tenantName];
  };

  const getBenefitOtherTenantName = (benefitType: BenefitNameEnum): string | undefined => {
    if (!profileBenefits.benefitTenant) return undefined;

    const benefitTenant = getTenant(profileBenefits.benefitTenant[benefitType]);

    if (benefitTenant?.toLowerCase() === tenantName.toLowerCase()) {
      return undefined;
    }

    return benefitTenant;
  };

  const benefitLists = getAvailableBenefitTypes().map((benefitType, index) => {
    if (benefitType === BenefitNameEnum.Azure) {
      return (
        <ProfileAzureBenefitDetail
          benefitType={BenefitNameEnum.Azure}
          benefits={profileBenefits[benefitNameToResultMap[benefitType]] ?? []}
          extendBenefitClicked={manageAzureBenefitCreditClicked}
          addAzureBenefitClicked={manageAddAzureBenefitClicked}
          canManageBenefits={canManageBenefits}
          benefitOtherTenantName={getBenefitOtherTenantName(benefitType)}
        />
      );
    } else if (benefitType === BenefitNameEnum.VisualStudio) {
      return (
        <VisualStudioBenefitDetail
          key={index}
          benefitType={BenefitNameEnum.VisualStudio}
          benefits={profileBenefits[benefitNameToResultMap[benefitType]] ?? []}
          assignBenefitClicked={assignBenefitClicked}
          canManageBenefits={canManageBenefits && tenantName === TenantTypesEnum.MSP}
          benefitOtherTenantName={getBenefitOtherTenantName(benefitType)}
        />
      );
    } else if (benefitType === BenefitNameEnum.M365Copilot) {
      return (
        <M365CopilotBenefitDetail
          benefits={profileBenefits[benefitNameToResultMap[benefitType]] ?? []}
        />
      );
    } else if (benefitType === BenefitNameEnum.BenefitManager) {
      // Used to render the benefit manager list using YAML.
      return (
        <BenefitManagementContainer
          benefitManager={profileBenefits.benefitManagerItems ?? []}
          canManageBenefits={canManageBenefits}
          userProfileId={parseInt(id)}
          assignBenefitClicked={assignBenefitClicked}
        />
      );
    } else {
      return (
        <KeyBenefitsList
          key={index}
          benefitType={benefitType}
          benefits={profileBenefits[benefitNameToResultMap[benefitType]] ?? []}
          assignBenefitClicked={assignBenefitClicked}
          canManageBenefits={canManageBenefits}
          userProfileId={parseInt(id)}
          editM365DetailsClicked={editM365DetailsClicked}
          m365FetchErrorMessage={m365FetchErrorMessage}
          m365FetchStatus={m365FetchStatus}
          m365BenefitDetail={m365BenefitDetail}
          editMS365DetailsClicked={editMS365DetailsClicked}
          ms365FetchErrorMessage={ms365FetchErrorMessage}
          ms365FetchStatus={ms365FetchStatus}
          ms365BenefitDetail={ms365BenefitDetail}
          teamsFetchStatus={teamsFetchStatus}
          teamsFetchErrorMessage={teamsFetchErrorMessage}
          awardKitsFetchStatus={awardKitsFetchStatus}
          awardKitsFetchErrorMessage={awardKitsFetchErrorMessage}
          benefitOtherTenantName={getBenefitOtherTenantName(benefitType)}
        />
      );
    }
  });

  useEffect(() => {
    if (isClaimingBenefitStatus === OperationStatus.Succeeded) {
      dismissAssignPanel();
    }
  }, [isClaimingBenefit, isClaimingBenefitStatus, claimingBenefitError]);

  // if the M365 Update Detail is succeed, close the side panel.
  useEffect(() => {
    if (m365UpdateStatus === OperationStatus.Succeeded) {
      dismissEditM365Panel();
    }
  }, [m365UpdateStatus, m365UpdateErrorMessage]);

  useEffect(() => {
    if (extendAzureBenefitCreditUpdateStatus === OperationStatus.Succeeded) {
      dismissExtendAzureBenefitCreditPanel();
    }
  }, [extendAzureBenefitCreditUpdateStatus]);

  useEffect(() => {
    if (addAzureBenefitsUpdateStatus === OperationStatus.Succeeded) {
      dismissAddAzureBenefitPanel();
    }
  }, [addAzureBenefitsUpdateStatus]);
  const onChangeOfSku = (option: any) => setSku(option.key);

  /**
   * Get benefit name
   * @returns Title of Assign Side Panel
   */
  const getBenefitDisplayTitle = (): string => {
    const title =
      benefitToClaim === BenefitNameEnum.M365
        ? BenefitDisplayNameEnum.O365
        : benefitToClaim === BenefitNameEnum.MS365
        ? BenefitDisplayNameEnum.MS365
        : benefitToClaim;
    return title;
  };

  return (
    <>
      {profileBenefitsStatus === OperationStatus.Failed ? (
        <Text>{profileBenefitsErrorMessage}</Text>
      ) : profileBenefitsStatus === OperationStatus.Pending ? (
        <Spinner size={SpinnerSize.large} />
      ) : (
        <>
          <SidePanel
            isOpen={isAssignPanelOpen}
            title="Add Benefits"
            saveButtonLabel="Claim"
            error={claimingBenefitError}
            disableActions={isClaimingBenefit}
            onDismiss={dismissAssignPanel}
            onSave={claimBenefitDispatch}
          >
            <Stack>
              <Text>You can claim a benefit and assign to active users</Text>
            </Stack>
            <Stack horizontal tokens={horizontalGapStackTokens}>
              <StackItem>
                <Text styles={benefitLabelStyles}>Benefit:</Text>
              </StackItem>
              <StackItem>
                <Text>{getBenefitDisplayTitle()}</Text>
              </StackItem>
            </Stack>
            <Stack styles={containerStyles}>
              {benefitToClaim === BenefitNameEnum.AwardKits && (
                <>
                  <ComboBoxController
                    control={control}
                    errors={errors}
                    inputName="AwardType"
                    labelDisplayName="Award Type"
                    options={AwardkitMilestone}
                    defaultSelectedKey={Sku}
                    onChange={(_e): void => {
                      onChangeOfSku(_e);
                    }}
                  />
                </>
              )}
            </Stack>
            <Stack>
              <Text>
                {`Assign a ${getBenefitDisplayTitle()} code to a Community Leader. This code will be available
                for the user under Account - Benefits - ${getBenefitDisplayTitle()}. You should notify
                the user that a new code has been assigned.`}
              </Text>
            </Stack>
          </SidePanel>

          {isEditM365PanelOpen && (
            <M365DetailsEditForm
              benefitType={BenefitNameEnum.O365}
              isEditM365PanelOpen={isEditM365PanelOpen}
              setIsEditM365PanelOpen={setIsEditM365PanelOpen}
              userProfileId={parseInt(id)}
              dismissPanel={dismissEditM365Panel}
              m365UpdateErrorMessage={m365UpdateErrorMessage}
              updateM365BenefitDetail={updateM365BenefitDetail}
              m365BenefitDetail={m365BenefitDetail}
            />
          )}

          {isEditMS365PanelOpen && (
            <M365DetailsEditForm
              benefitType={BenefitNameEnum.MS365}
              isEditM365PanelOpen={isEditMS365PanelOpen}
              setIsEditM365PanelOpen={setIsEditMS365PanelOpen}
              userProfileId={parseInt(id)}
              dismissPanel={dismissEditMS365Panel}
              m365UpdateErrorMessage={ms365UpdateErrorMessage}
              updateMS365BenefitDetail={updateMS365BenefitDetail}
              m365BenefitDetail={ms365BenefitDetail}
            />
          )}

          <AzureBenefitExtendCreditForm
            isPanelOpen={isExtendAzureCreditPanelOpen}
            setIsPanelOpen={setIsExtendAzureCreditPanelOpen}
            dismissPanel={dismissExtendAzureBenefitCreditPanel}
            extendAzureBenefitCredit={extendAzureBenefitCreditDispatch}
            errorMessage={extendAzureBenefitCreditErrorMessage}
            isSaving={extendAzureBenefitCreditUpdateStatus === OperationStatus.Pending}
          />
          <AzureBenefitAddRecordForm
            isPanelOpen={isAddAzureBenefitPanelOpen}
            setIsPanelOpen={setIsAddAzureBenefitPanelOpen}
            dismissPanel={dismissAddAzureBenefitPanel}
            addAzureBenefitCredit={addAzureBenefitsDispatch}
            errorMessage={addAzureBenefitsErrorMessage}
            isSaving={addAzureBenefitsUpdateStatus === OperationStatus.Pending}
          />

          <>{benefitLists}</>
        </>
      )}
    </>
  );
};

export default ProfileBenefitDetails;
